/**
 * Card Generator Module
 * Generates HTML for document cards
 */

"use strict";

// Check if debug mode is enabled (global)
const urlParams = new URLSearchParams(window.location.search);


import { SciDoc } from './document-card.js';
import { getMatchConditions, myEach } from '../search/search-conditions.js';
import { encodeFilePath } from '../../modules/files/file-list.utils.js';

/**
 * Generate document card and append to container
 * @param {string} docName - Document name
 * @param {string} docLink - Document URL
 * @param {number} docSize - Document size
 * @param {jQuery} appendTo - Container to append to
 * @param {string} context - Optional context/extract
 */
export function generateCard(docName, docLink, docSize, appendTo, context = "") {
    if (window.HyperLog) {
        HyperLog.debug("generateCard started", { 
            docName: docName,
            docLink: docLink,
            docSize: docSize,
            hasContext: !!context
        });
    }
    
    // Clone template
    const newCard = $(".document-card-template").clone(true, true);
    newCard.removeClass('d-none document-card-template');
    
    const cardBody = newCard.find(".card-body");
    
    // Set context if provided
    if (context) {
        cardBody.find(".card-extract").html(context);
    }
    
    // Create document object
    const doc = new SciDoc(docName, docLink, docSize);
    
    // Generate card ID
    const cardId = generateCardId(doc);
    newCard.attr('id', cardId);
    
    // Set card header
    setCardHeader(newCard, doc);
    
    // Set card body content
    setCardBody(cardBody, doc);
    
    // Set card actions
    setCardActions(newCard, doc);
    
    // Find related documents if it's a bulletin
    if (doc.isBulletin() && doc.hasDate()) {
        findRelatedDocuments(cardBody, doc);
    }
    
    // Append to container
    newCard.appendTo(appendTo);
    
    // Mark as loaded
    newCard.addClass('card-loaded');
    
    // Trigger loaded event
    newCard.trigger('card:loaded', [doc]);
    
    if (window.HyperLog) {
        HyperLog.debug("Card generated", {
            cardId: cardId,
            docType: doc.docMaterialType
        });
    }
}

/**
 * Generate unique card ID
 * @param {SciDoc} doc - Document object
 * @returns {string} Card ID
 */
function generateCardId(doc) {
    const sanitized = doc.fileName.replace(/[^a-zA-Z0-9]/g, '-');
    return `card-${sanitized}-${Date.now()}`;
}

/**
 * Set card header content
 * @param {jQuery} card - Card element
 * @param {SciDoc} doc - Document object
 */
function setCardHeader(card, doc) {
    const header = card.find('.card-header');
    
    // Document type badge
    const typeBadge = createTypeBadge(doc);
    header.find('.card-type-badge').html(typeBadge);
    
    // Document title
    const title = createCardTitle(doc);
    header.find('.card-title').html(title);
    
    // Document date
    if (doc.hasDate()) {
        const dateStr = doc.getFormattedDate();
        header.find('.card-date').text(dateStr).removeClass('d-none');
    }
    
    // File info
    header.find('.card-file-info').html(createFileInfo(doc));
}

/**
 * Create type badge HTML
 * @param {SciDoc} doc - Document object
 * @returns {string} Badge HTML
 */
function createTypeBadge(doc) {
    const typeColors = {
        'HCOB': 'bg-primary',
        'HCOPL': 'bg-success',
        'PAB': 'bg-info',
        'BTB': 'bg-warning',
        'BPL': 'bg-secondary',
        'FO': 'bg-error',
        'ED': 'bg-dark'
    };
    
    const color = typeColors[doc.docMaterialType] || 'bg-secondary';
    const techType = doc.docTechType === 'Tech' ? ' (Tech)' : 
                    doc.docTechType === 'Admin' ? ' (Admin)' : '';
    
    return `<span class="badge ${color}">${doc.docMaterialType}${techType}</span>`;
}

/**
 * Create card title
 * @param {SciDoc} doc - Document object
 * @returns {string} Title HTML
 */
function createCardTitle(doc) {
    // Highlight search terms in title
    const query = $('#fnd_searchbox').val();
    if (query && window.cachedRequest && window.cachedRequest.mCond) {
        return myEach(doc.searchName, window.cachedRequest.mCond, false, true) || doc.searchName;
    }
    return doc.searchName;
}

/**
 * Create file info HTML
 * @param {SciDoc} doc - Document object
 * @returns {string} File info HTML
 */
function createFileInfo(doc) {
    const size = formatFileSize(doc.fileSize);
    const ext = doc.fileExt.toUpperCase();
    
    return `<small class="text-muted">${ext} • ${size}</small>`;
}

/**
 * Format file size
 * @param {number} bytes - Size in bytes
 * @returns {string} Formatted size
 */
function formatFileSize(bytes) {
    // Normalize input: if value looks like KB (small ints), assume bytes already
    bytes = Number(bytes) || 0;
    if (bytes < 1000) return bytes + ' B';
    if (bytes < 1000000) return Math.round(bytes / 1000) + ' KB';
    if (bytes < 1000000000) return Math.round(bytes / 1000000) + ' MB';
    return Math.round(bytes / 1000000000) + ' GB';
}

/**
 * Set card body content
 * @param {jQuery} cardBody - Card body element
 * @param {SciDoc} doc - Document object
 */
function setCardBody(cardBody, doc) {
    // File path
    const pathHtml = createPathBreadcrumb(doc);
    cardBody.find('.card-path').html(pathHtml);
    
    // Document metadata
    const metadata = createMetadataList(doc);
    cardBody.find('.card-metadata').html(metadata);
    
    // Initialize tabs if needed
    if (cardBody.find('.nav-tabs').length > 0) {
        initializeCardTabs(cardBody);
    }
}

/**
 * Create path breadcrumb
 * @param {SciDoc} doc - Document object
 * @returns {string} Breadcrumb HTML
 */
function createPathBreadcrumb(doc) {
    const pathParts = doc.filePath.split('/').filter(p => p);
    const breadcrumbs = pathParts.map((part, index) => {
        const isLast = index === pathParts.length - 1;
        return `<span class="path-part ${isLast ? 'text-primary' : ''}">${part}</span>`;
    }).join(' <i class="bi bi-chevron-right text-muted small"></i> ');
    
    return breadcrumbs;
}

/**
 * Create metadata list
 * @param {SciDoc} doc - Document object
 * @returns {string} Metadata HTML
 */
function createMetadataList(doc) {
    const items = [];
    
    // Language detection
    const lang = detectLanguage(doc.filePath);
    if (lang) {
        items.push(`<li><strong>Язык:</strong> ${lang}</li>`);
    }
    
    // Source detection
    const source = detectSource(doc.filePath);
    if (source) {
        items.push(`<li><strong>Источник:</strong> ${source}</li>`);
    }
    
    // Format type
    const format = detectFormat(doc.filePath);
    if (format) {
        items.push(`<li><strong>Формат:</strong> ${format}</li>`);
    }
    
    // File extension
    items.push(`<li><strong>Тип файла:</strong> ${doc.fileExt.toUpperCase()}</li>`);
    
    // File size
    items.push(`<li><strong>Размер:</strong> ${formatFileSize(doc.fileSize)}</li>`);
    
    return `<ul class="list-unstyled mb-0">${items.join('')}</ul>`;
}

/**
 * Detect language from path
 * @param {string} path - File path
 * @returns {string} Language name
 */
function detectLanguage(path) {
    const langMap = {
        'English': 'Английский',
        'Russian': 'Русский',
        'German': 'Немецкий',
        'French': 'Французский',
        'Spanish': 'Испанский'
    };
    
    for (const [key, value] of Object.entries(langMap)) {
        if (path.includes(`/${key}/`)) {
            return value;
        }
    }
    return '';
}

/**
 * Detect source from path
 * @param {string} path - File path
 * @returns {string} Source name
 */
function detectSource(path) {
    if (path.includes('/FP/')) return 'FP';
    if (path.includes('/Hauri/')) return 'Hauri';
    if (path.includes('/Other/')) return 'Other';
    return '';
}

/**
 * Detect format from path
 * @param {string} path - File path
 * @returns {string} Format name
 */
function detectFormat(path) {
    if (path.includes('/AUDIO/')) return 'Аудио';
    if (path.includes('/SCAN/')) return 'Скан';
    if (path.includes('/TEXT/')) return 'Текст';
    if (path.includes('/VIDEO/')) return 'Видео';
    return '';
}

/**
 * Set card actions/buttons
 * @param {jQuery} card - Card element  
 * @param {SciDoc} doc - Document object
 */
function setCardActions(card, doc) {
    if (debugMode === '1') {
                console.log('[setCardActions] Setting card actions for:', doc.fileName);
            }
    if (debugMode === '1') {
                console.log('[setCardActions] Document linkDoc:', doc.linkDoc);
            }
    if (debugMode === '1') {
                console.log('[setCardActions] Document linkPdf:', doc.linkPdf);
            }
    
    // Set main title link
    const titleLink = card.find('.card-title2');
    if (titleLink.length > 0) {
        // Encode path if it's a relative path (not starting with http:// or https://)
        const href = doc.linkDoc.startsWith('http://') || doc.linkDoc.startsWith('https://') 
            ? doc.linkDoc 
            : encodeFilePath(doc.linkDoc);
        titleLink.text(doc.title).attr("href", href);
        console.log('[setCardActions] Title link set to:', href);
    }
    
    // Set document download button
    const docDownloadBtn = card.find('.card-download-doc');
    if (docDownloadBtn.length > 0) {
        // Encode path if it's a relative path (not starting with http:// or https://)
        const href = doc.linkDoc.startsWith('http://') || doc.linkDoc.startsWith('https://') 
            ? doc.linkDoc 
            : encodeFilePath(doc.linkDoc);
        docDownloadBtn.attr("href", href);
        console.log('[setCardActions] Doc download button set to:', href);
    }
    
    // Set PDF download button  
    const pdfDownloadBtn = card.find('.card-download-pdf');
    if (pdfDownloadBtn.length > 0) {
        // Encode path if it's a relative path (not starting with http:// or https://)
        const href = doc.linkPdf.startsWith('http://') || doc.linkPdf.startsWith('https://') 
            ? doc.linkPdf 
            : encodeFilePath(doc.linkPdf);
        pdfDownloadBtn.attr("href", href);
        console.log('[setCardActions] PDF download button set to:', href);
    }
    
    // Set date display
    const dateElement = card.find('.card-date');
    if (dateElement.length > 0 && doc.dDate) {
        dateElement.text(doc.dDate);
        if (debugMode === '1') {
                console.log('[setCardActions] Date set to:', doc.dDate);
            }
    }
    
    // Set issue if available
    if (doc.dIssue && doc.dIssue !== "") {
        const issueElement = card.find('.card-issue');
        issueElement.removeClass("d-none").find(".card-issue-paste").text(doc.dIssue);
        if (debugMode === '1') {
                console.log('[setCardActions] Issue set to:', doc.dIssue);
            }
    }
    
    // Set revision if available
    if (doc.dRevNum && doc.dRevNum !== "") {
        const revisionElement = card.find('.card-revision');
        revisionElement.removeClass("d-none").find(".card-revision-paste").text(doc.dRevNum);
        if (debugMode === '1') {
                console.log('[setCardActions] Revision set to:', doc.dRevNum);
            }
    }
    
    if (debugMode === '1') {
                console.log('[setCardActions] Card actions setup completed for:', doc.fileName);
            }
}

/**
 * Initialize card tabs
 * @param {jQuery} cardBody - Card body element
 */
function initializeCardTabs(cardBody) {
    const tabs = cardBody.find('.nav-tabs .nav-link');
    tabs.on('click', function(e) {
        e.preventDefault();
        const tab = new bootstrap.Tab(this);
        tab.show();
    });
}

/**
 * Find related documents
 * @param {jQuery} cardBody - Card body element
 * @param {SciDoc} doc - Document object
 */
function findRelatedDocuments(cardBody, doc) {
    const relatedContainer = cardBody.find('.related-documents');
    if (relatedContainer.length === 0) return;
    
    // Build search query for related documents
    const searchQuery = doc.dYear + doc.dMonth + doc.dDay2 + (doc.dIssue || '');
    const searchConditions = getMatchConditions(searchQuery, true);
    
    // Add document type filter
    const typeFilter = {
        "condition": "and",
        "value": [{
            "condition": "or",
            "value": [
                new RegExp(`/${doc.docMaterialType}/`, 'i'),
                new RegExp(`/${doc.docMaterialType === 'HCOB' ? 'БОХС' : doc.docMaterialType}/`, 'i')
            ],
            "modifier": ""
        }]
    };
    
    searchConditions.and = searchConditions.and || [];
    searchConditions.and.push(typeFilter);
    
    // Search for matches
    const sourceFiles = (window.filesStore && typeof window.filesStore.getFiles === 'function')
        ? window.filesStore.getFiles() : [];
    const matches = myEach(sourceFiles, searchConditions, true, false);
    
    if (matches.length > 0) {
        displayRelatedDocuments(relatedContainer, matches, doc);
    }
}

/**
 * Display related documents
 * @param {jQuery} container - Container element
 * @param {Array} matches - Matched documents
 * @param {SciDoc} currentDoc - Current document
 */
function displayRelatedDocuments(container, matches, currentDoc) {
    const list = $('<ul class="list-group list-group-flush"></ul>');
    
    matches.forEach(match => {
        if (match.n === currentDoc.fileName) return; // Skip current document
        
        const item = $(`
            <li class="list-group-item d-flex justify-content-between align-items-center">
                <a href="${window.CLOUD_URL}${match.p}/${match.n}" target="_blank" class="text-decoration-none">
                    <i class="bi bi-file-earmark me-2"></i>${match.n}
                </a>
                <span class="badge bg-secondary rounded-pill">${formatFileSize(match.s)}</span>
            </li>
        `);
        
        list.append(item);
    });
    
    container.html('<h6 class="mb-2">Связанные документы:</h6>');
    container.append(list);
}

/**
 * Copy text to clipboard
 * @param {string} text - Text to copy
 */
function copyToClipboard(text) {
    if (navigator.clipboard) {
        navigator.clipboard.writeText(text);
    } else {
        // Fallback for older browsers
        const textarea = $('<textarea>').val(text).css({
            position: 'absolute',
            left: '-9999px'
        });
        $('body').append(textarea);
        textarea[0].select();
        document.execCommand('copy');
        textarea.remove();
    }
}

/**
 * Show tooltip temporarily
 * @param {jQuery} element - Element to show tooltip on
 * @param {string} message - Tooltip message
 */
function showTooltip(element, message) {
    const tooltip = new bootstrap.Tooltip(element[0], {
        title: message,
        trigger: 'manual'
    });
    
    tooltip.show();
    setTimeout(() => {
        tooltip.hide();
        tooltip.dispose();
    }, 2000);
}

/**
 * Search for similar documents
 * @param {SciDoc} doc - Document to search similar for
 */
function searchSimilar(doc) {
    const searchBox = $('#fnd_searchbox');
    searchBox.val(doc.searchName);
    searchBox.trigger('input');
    $('.fnd_searchform').submit();
}

// Export for global access
window.generateCard = generateCard;